import React, { useCallback, useEffect, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, Stripe, StripeConstructorOptions } from '@stripe/stripe-js'
import locales from '@/constants/locales'
import { useTellYourStoryPrompt } from '@/organisms/TellYourStoryPrompt/TellYourStoryPrompt'
import { Offer } from '@/services/PifService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { PayItForwardStripeForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm'
import { useClientSecret } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useClientSecret'
import { PayItForwardFormProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'
import { usePayItForwardState } from './usePayItForwardState'

export interface PaymentMethodExperimentProps {
  projectSlug: string
  offer: Offer
  isShowThanksForSharing: boolean
}

export const PaymentMethods: React.FC<PaymentMethodExperimentProps> = ({
  projectSlug,
  offer,
  isShowThanksForSharing,
}) => {
  const { locale } = useLocale()
  const { isSubscription, onComplete: onCompletePifState } = usePayItForwardState()
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const { openModal: openTellYourStoryModal, setRelatedOrderId } = useTellYourStoryPrompt()
  const { user, loading } = useUser()
  const { clientSecret } = useClientSecret({ offer, user: user, userLoading: loading })

  const stripeLocales = locales.stripeLocales as Record<string, StripeConstructorOptions['locale']>

  useEffect(() => {
    setStripePromise(
      loadStripe(`${process.env.NEXT_PUBLIC_STRIPE_KEY}`, { locale: stripeLocales[locale as string] || 'en' }),
    )
  }, [locale, stripeLocales])

  const openTellYourStoryModalWhenApplicable = useCallback(() => {
    if (isShowThanksForSharing && Boolean(user)) {
      openTellYourStoryModal()
    }
  }, [isShowThanksForSharing, openTellYourStoryModal, user])

  const onComplete = useCallback(
    ({ orderId }: { orderId: string }) => {
      onCompletePifState()
      setRelatedOrderId(orderId)
      openTellYourStoryModalWhenApplicable()
    },
    [onCompletePifState, openTellYourStoryModalWhenApplicable, setRelatedOrderId],
  )

  if (clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ loader: 'auto', clientSecret }}>
        <PayItForwardFormProvider
          onComplete={onComplete}
          offer={offer}
          projectSlug={projectSlug}
          isSubscription={isSubscription}
          user={user}
        >
          <PayItForwardStripeForm
            clientSecret={clientSecret}
            offer={offer}
            isSubscription={isSubscription}
            projectSlug={projectSlug}
          />
        </PayItForwardFormProvider>
      </Elements>
    )
  }
  return null
}
